import { SpellCheckMatch } from '@/services/spellCheckerService';

/**
 * Error categorization logic that works for both free trial and premium responses
 */
const errorCategories = {
  spelling: (match: SpellCheckMatch) => 
    match.rule.issueType === 'misspelling' ||
    match.type.typeName === 'UnknownWord' ||
    match.rule.category.id === 'TYPOS' ||
    match.rule.category.id === 'CONFUSED_WORDS' ||
    match.rule.id.includes('ORTHOGRAPHY') ||
    match.rule.id.includes('SPELLING') ||
    match.rule.id.includes('MORFOLOGIK') ||
    match.message.toLowerCase().includes('spelling') ||
    match.shortMessage.toLowerCase().includes('spelling') ||
    match.message.toLowerCase().includes('spell') ||
    match.shortMessage.toLowerCase().includes('spell'),

  grammar: (match: SpellCheckMatch) => 
    match.rule.id.includes('GRAMMAR') ||
    match.message.toLowerCase().includes('verb') ||
    match.message.toLowerCase().includes('tense') ||
    match.message.toLowerCase().includes('agreement') ||
    match.message.toLowerCase().includes('subject') ||
    match.message.toLowerCase().includes('pronoun') ||
    match.message.toLowerCase().includes('article') ||
    match.message.toLowerCase().includes('preposition') ||
    match.message.toLowerCase().includes('conjunction') ||
    match.message.toLowerCase().includes('participle') ||
    match.message.toLowerCase().includes('infinitive') ||
    match.message.toLowerCase().includes('modal') ||
    match.message.toLowerCase().includes('auxiliary') ||
    // Include punctuation in grammar
    match.message.toLowerCase().includes('comma') ||
    match.message.toLowerCase().includes('apostrophe') ||
    match.message.toLowerCase().includes('period') ||
    match.message.toLowerCase().includes('semicolon') ||
    match.message.toLowerCase().includes('colon') ||
    match.message.toLowerCase().includes('quotation') ||
    match.message.toLowerCase().includes('hyphen') ||
    match.message.toLowerCase().includes('dash') ||
    match.rule.category.id === 'PUNCTUATION',

  style: (match: SpellCheckMatch) => 
    match.message.toLowerCase().includes('informal') ||
    match.message.toLowerCase().includes('formal') ||
    match.message.toLowerCase().includes('wordy') ||
    match.message.toLowerCase().includes('redundant') ||
    match.message.toLowerCase().includes('passive') ||
    match.message.toLowerCase().includes('clarity')
};

/**
 * Determines if a spell check match is a spelling error
 * @param match - The spell check match to evaluate
 * @returns true if the match is a spelling error, false otherwise
 */
export const isSpellingError = (match: SpellCheckMatch): boolean => {
  return errorCategories.spelling(match);
};

/**
 * Determines if a spell check match is a grammar error (including punctuation)
 * @param match - The spell check match to evaluate
 * @returns true if the match is a grammar error, false otherwise
 */
export const isGrammarError = (match: SpellCheckMatch): boolean => {
  return errorCategories.grammar(match);
};

/**
 * Determines if a spell check match is a style error
 * @param match - The spell check match to evaluate
 * @returns true if the match is a style error, false otherwise
 */
export const isStyleError = (match: SpellCheckMatch): boolean => {
  return errorCategories.style(match);
};

/**
 * Filters spell check results to get only spelling errors
 * @param matches - Array of spell check matches
 * @returns Array of spelling errors only
 */
export const getSpellingErrors = (matches: SpellCheckMatch[]): SpellCheckMatch[] => {
  return matches.filter(isSpellingError);
};

/**
 * Filters spell check results to get only grammar errors (including punctuation)
 * @param matches - Array of spell check matches
 * @returns Array of grammar errors only
 */
export const getGrammarErrors = (matches: SpellCheckMatch[]): SpellCheckMatch[] => {
  return matches.filter(isGrammarError);
};

/**
 * Filters spell check results to get only style errors
 * @param matches - Array of spell check matches
 * @returns Array of style errors only
 */
export const getStyleErrors = (matches: SpellCheckMatch[]): SpellCheckMatch[] => {
  return matches.filter(isStyleError);
};

/**
 * Categorizes all errors into their respective types
 * @param matches - Array of spell check matches
 * @returns Object with categorized errors
 */
export const categorizeErrors = (matches: SpellCheckMatch[]) => {
  return {
    spelling: getSpellingErrors(matches),
    grammar: getGrammarErrors(matches),
    style: getStyleErrors(matches)
  };
}; 